import React from 'react';
import { Info, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type VariantProps } from 'class-variance-authority';
import { riskScoreBand, ScoreBand } from 'src/shared/components/risk-score-cell/riskScoreColour';
import RiskBarometer from 'src/shared/components/risk-barometer/RiskBarometer';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { Button } from 'src/shared/components/ui/button';
import { Badge, badgeVariants } from 'src/shared/components/ui/badge';

export type ScoreType = 'Risk' | 'Condition';

export type RiskScoreCardProps = {
    title: string;
    value: number | null;
    scoreType: ScoreType;
    isLoading?: boolean;
    /** When provided, renders an "Explore" link (react-router) in the card header. */
    exploreTo?: string;
    size?: 'lg' | 'md';
    icon?: React.ComponentType<{ className?: string }>;
    slug?: string;
    explainerSlug?: string;
    portfolioRank?: { rank: number; total: number } | null;
    scoreLabel?: string;
    showBarometer?: boolean;
    fullBadgeLabel?: boolean;
    className?: string;
};

const BADGE_TEXT: Record<ScoreType, Record<ScoreBand, string>> = {
    Risk: { 1: 'Very High', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Very Low' },
    Condition: { 1: 'Very Poor', 2: 'Poor', 3: 'Moderate', 4: 'Good', 5: 'Very Good' },
};

const BAROMETER_LABELS: Record<ScoreType, { lowLabel: string; highLabel: string }> = {
    Risk: { lowLabel: 'V. Low', highLabel: 'V. High' },
    Condition: { lowLabel: 'V. Poor', highLabel: 'V. Good' },
};

const BADGE_VARIANT: Record<ScoreBand, VariantProps<typeof badgeVariants>['variant']> = {
    1: 'status-very-high',
    2: 'status-high',
    3: 'status-medium',
    4: 'status-low',
    5: 'status-very-low',
};

const RiskScoreCard = ({
    title,
    value,
    scoreType: riskLabel,
    isLoading = false,
    size = 'lg',
    icon: Icon,
    slug = '',
    explainerSlug,
    portfolioRank,
    scoreLabel,
    showBarometer = true,
    fullBadgeLabel = true,
    className,
    exploreTo,
}: RiskScoreCardProps) => {
    if (isLoading) {
        return (
            <div className='bg-white shadow-portfolio-summary-card rounded-lg p-4 flex flex-col gap-3 animate-pulse'>
                <div className='h-4 bg-grey-20 rounded w-1/2' />
                <div className='h-12 bg-grey-20 rounded w-1/3' />
                <div className='h-3 bg-grey-20 rounded w-full' />
                <div className='h-4 bg-grey-20 rounded w-3/4' />
            </div>
        );
    }

    const direction = riskLabel === 'Risk' ? 'higher-worse' : 'higher-better';
    const band = value !== null ? riskScoreBand(value, direction) : null;
    const { lowLabel, highLabel } = BAROMETER_LABELS[riskLabel];
    const titleClass = size === 'lg' ? 'text-[20px] font-normal text-primary' : 'text-[16px] font-normal text-primary';
    const valueSizeClass = size === 'lg' ? 'text-[48px]' : 'text-[30px]';

    return (
        <div
            className={`justify-between bg-white shadow-portfolio-summary-card rounded-lg p-4 xl:p-5 flex flex-col gap-3${
                className ? ` ${className}` : ''
            }`}
        >
            <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-1'>
                    {Icon && <Icon className='h-5 w-5 text-primary' />}
                    <span className={titleClass}>{title}</span>
                    <QuestionButtonHelp
                        type='api'
                        placement='top'
                        slug={slug}
                        icon={<Info className='h-4 w-4 text-primary' />}
                        detailedExplainerSlug={explainerSlug}
                        showDetailedExplainerLink={!!explainerSlug}
                    />
                </div>
                {exploreTo && (
                    <Button asChild variant='outline' size='mini'>
                        <Link to={exploreTo}>
                            Explore
                            <ChevronRight />
                        </Link>
                    </Button>
                )}
            </div>

            <div className='flex flex-col gap-3'>
                {portfolioRank ? (
                    <div className='flex flex-col gap-1'>
                        <p className='leading-none'>
                            <span className='text-[48px] font-normal text-black'>#{portfolioRank.rank}</span>
                            <span className='text-[20px] font-normal text-grey-80 ml-1'>/{portfolioRank.total} sites</span>
                        </p>
                        {scoreLabel && value !== null && (
                            <span className='text-xs text-grey-80'>
                                {scoreLabel}: {value.toFixed(2)}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className='flex items-center gap-3'>
                        <span className={`${valueSizeClass} font-normal text-black leading-none`}>
                            {value !== null ? value.toFixed(2) : '—'}
                        </span>
                        {band && (
                            <Badge data-testid='risk-score-card-badge' variant={BADGE_VARIANT[band]}>
                                {BADGE_TEXT[riskLabel][band]} {fullBadgeLabel && riskLabel}
                            </Badge>
                        )}
                    </div>
                )}

                <span className='text-xs font-normal text-grey-80'>0-1 weighted average</span>

                {showBarometer && <RiskBarometer value={value} direction={direction} lowLabel={lowLabel} highLabel={highLabel} />}
            </div>
        </div>
    );
};

export default RiskScoreCard;
